import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import StudioSidebar from '../../components/ui/StudioSidebar';
import { studioService } from '../../utils/studioService';
import { supabase } from '../../utils/supabase';

const StudioGameUpload = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [studio, setStudio] = useState(null);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [showChecklist, setShowChecklist] = useState(true);
  const [checklistItems, setChecklistItems] = useState({
    hasApiKey: false,
    hasImplementedSDK: false,
    understands: false
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    contentRating: 'E',
    gamedistribution_url: '',
    tier: 'premium'
  });

  useEffect(() => {
    if (!user || (userProfile?.role !== 'studio_admin')) {
      navigate('/');
      return;
    }

    loadInitialData();
  }, [user, userProfile, navigate]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get studio info
      const { data: studioData, error: studioError } = await studioService.getStudioByOwner(user.id);

      if (studioError || !studioData) {
        setError('No studio associated with this account');
        return;
      }

      setStudio(studioData);

      // Load game categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('game_categories')
        .select('name')
        .order('name');

      if (!categoriesError && categoriesData) {
        setCategories(categoriesData.map(cat => ({ value: cat.name, label: cat.name })));
      } else {
        // Fallback categories
        setCategories([
          { value: 'Action', label: 'Action' },
          { value: 'Adventure', label: 'Adventure' },
          { value: 'Puzzle', label: 'Puzzle' },
          { value: 'Strategy', label: 'Strategy' },
          { value: 'Racing', label: 'Racing' }
        ]);
      }
    } catch (err) {
      setError('Failed to load studio data');
      console.error('Error loading studio data:', err);
    } finally {
      setLoading(false);
    }
  };

  const contentRatings = [
    { value: 'E', label: 'Everyone' },
    { value: 'E10+', label: 'Everyone 10+' },
    { value: 'T', label: 'Teen' },
    { value: 'M', label: 'Mature 17+' },
    { value: 'AO', label: 'Adults Only 18+' },
    { value: 'RP', label: 'Rating Pending' }
  ];

  const tierOptions = [
    { value: 'free', label: 'Free' },
    { value: 'premium', label: 'Premium' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Game title is required');
      return false;
    }
    if (!formData.description.trim()) {
      setError('Game description is required');
      return false;
    }
    if (!formData.category) {
      setError('Please select a category');
      return false;
    }
    if (!formData.gamedistribution_url.trim()) {
      setError('Game URL is required');
      return false;
    }
    if (!formData.gamedistribution_url.match(/^https?:\/\/.+/)) {
      setError('Please enter a valid URL starting with http:// or https://');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Get or create category
      let categoryId = null;
      const { data: existingCategory } = await supabase
        .from('game_categories')
        .select('id')
        .eq('name', formData.category)
        .single();

      if (existingCategory) {
        categoryId = existingCategory.id;
      } else {
        const { data: newCategory, error: categoryError } = await supabase
          .from('game_categories')
          .insert({ name: formData.category })
          .select('id')
          .single();

        if (categoryError) {
          throw new Error('Failed to create category');
        }
        categoryId = newCategory.id;
      }

      // Create game with 'under_review' status
      const { data: newGame, error: gameError } = await supabase
        .from('games')
        .insert({
          title: formData.title,
          description: formData.description,
          studio_id: studio.id,
          rating: formData.contentRating,
          status: 'under_review',
          tier: formData.tier,
          game_type: 'gamedistribution',
          game_structure_type: 'external',
          webgpu_url: formData.gamedistribution_url,
          play_count: 0,
          average_rating: 0
        })
        .select()
        .single();

      if (gameError) {
        throw new Error(gameError.message);
      }

      // Link game to category
      await supabase
        .from('game_category_mappings')
        .insert({
          game_id: newGame.id,
          category_id: categoryId
        });

      // Success! Navigate to studio games page
      navigate('/studio-games?uploaded=true');

    } catch (err) {
      setError(err.message || 'Failed to submit game for review');
      console.error('Game upload error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!studio) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">No Studio Found</h2>
          <p className="text-muted-foreground mb-4">{error || 'You need a studio to upload games'}</p>
          <Button onClick={() => navigate('/studio-dashboard')}>Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <StudioSidebar />

      <div className="lg:pl-72">
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border lg:hidden">
          <div className="px-4 py-4">
            <h1 className="text-xl font-bold text-foreground">Upload Game</h1>
          </div>
        </div>

        <main className="p-4 lg:p-8 pt-20 lg:pt-8 pb-20 lg:pb-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
              Submit a New Game
            </h1>
            <p className="text-muted-foreground">
              Submit your game for review. Once approved, it will be published on the platform.
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
            <div className="flex gap-3">
              <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Review Process</h3>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <li>• Your game will be submitted for admin review</li>
                  <li>• We typically review games within 1-2 business days</li>
                  <li>• You'll be notified once your game is approved</li>
                  <li>• Make sure your game URL is publicly accessible</li>
                </ul>
              </div>
            </div>
          </div>

          {/* SDK Integration Checklist */}
          {showChecklist && (
            <div className="bg-card border border-border rounded-lg p-6 mb-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">SDK Integration Checklist</h3>
                  <p className="text-sm text-muted-foreground">
                    Before uploading, make sure you've completed these important steps
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setShowChecklist(false)}
                />
              </div>

              <div className="space-y-4">
                {/* API Key */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="hasApiKey"
                    checked={checklistItems.hasApiKey}
                    onChange={(e) => setChecklistItems(prev => ({ ...prev, hasApiKey: e.target.checked }))}
                    className="mt-1 rounded border-border"
                  />
                  <div className="flex-1">
                    <label htmlFor="hasApiKey" className="text-sm font-medium text-foreground cursor-pointer">
                      I have generated an API key
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Required for SDK integration. <a href="/studio-api-keys" className="text-primary hover:underline">Generate one here</a>
                    </p>
                  </div>
                </div>

                {/* SDK Implementation */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="hasImplementedSDK"
                    checked={checklistItems.hasImplementedSDK}
                    onChange={(e) => setChecklistItems(prev => ({ ...prev, hasImplementedSDK: e.target.checked }))}
                    className="mt-1 rounded border-border"
                  />
                  <div className="flex-1">
                    <label htmlFor="hasImplementedSDK" className="text-sm font-medium text-foreground cursor-pointer">
                      I have integrated the Solis Games SDK
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      <a href="/studio-sdk" className="text-primary hover:underline">View SDK documentation</a>
                    </p>
                  </div>
                </div>

                {/* Understanding consequences */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="understands"
                    checked={checklistItems.understands}
                    onChange={(e) => setChecklistItems(prev => ({ ...prev, understands: e.target.checked }))}
                    className="mt-1 rounded border-border"
                  />
                  <div className="flex-1">
                    <label htmlFor="understands" className="text-sm font-medium text-foreground cursor-pointer">
                      I understand the benefits of SDK integration
                    </label>
                  </div>
                </div>
              </div>

              {/* Warning about not implementing SDK */}
              {(!checklistItems.hasApiKey || !checklistItems.hasImplementedSDK) && (
                <div className="mt-6 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <div className="flex gap-3">
                    <Icon name="AlertTriangle" size={20} className="text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-2">
                        Without SDK Integration, You'll Miss Out On:
                      </h4>
                      <ul className="text-sm text-yellow-800 dark:text-yellow-200 space-y-1">
                        <li>• <strong>Revenue Sharing:</strong> Can't track purchases for your 70% revenue split</li>
                        <li>• <strong>Detailed Analytics:</strong> No access to player behavior, session data, or engagement metrics</li>
                        <li>• <strong>Domain Locking:</strong> Your game won't be protected from unauthorized redistribution</li>
                        <li>• <strong>Cloud Saves:</strong> Players can't save their progress across devices</li>
                        <li>• <strong>Ad Revenue:</strong> No ability to serve ads or track ad performance</li>
                        <li>• <strong>Leaderboards:</strong> Can't implement competitive features</li>
                      </ul>
                      <p className="text-sm text-yellow-800 dark:text-yellow-200 mt-3">
                        <strong>Recommendation:</strong> Take 10-15 minutes to integrate the SDK before uploading.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Form */}
          <div className="bg-card border border-border rounded-lg p-6">
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2">
                  <Icon name="AlertCircle" size={20} className="text-destructive" />
                  <span className="text-sm text-destructive">{error}</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                label="Game Title"
                type="text"
                placeholder="Enter your game title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
                disabled={submitting}
              />

              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Description <span className="text-destructive">*</span>
                </label>
                <textarea
                  placeholder="Describe your game..."
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={submitting}
                  rows={5}
                  required
                />
              </div>

              <Select
                label="Category"
                options={categories}
                value={formData.category}
                onChange={(value) => handleInputChange('category', value)}
                placeholder="Select a category"
                required
                disabled={submitting}
              />

              <Select
                label="Content Rating"
                options={contentRatings}
                value={formData.contentRating}
                onChange={(value) => handleInputChange('contentRating', value)}
                required
                disabled={submitting}
              />

              <Select
                label="Tier"
                options={tierOptions}
                value={formData.tier}
                onChange={(value) => handleInputChange('tier', value)}
                required
                disabled={submitting}
                description="Premium games are only accessible to subscribers"
              />

              <Input
                label="Game URL"
                type="url"
                placeholder="https://yourgame.com/play"
                value={formData.gamedistribution_url}
                onChange={(e) => handleInputChange('gamedistribution_url', e.target.value)}
                description="The URL where your game is hosted"
                required
                disabled={submitting}
              />

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/studio-games')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  loading={submitting}
                  disabled={submitting}
                  iconName="Send"
                >
                  Submit for Review
                </Button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default StudioGameUpload;
